import { useQuery } from "@tanstack/react-query";
import cx from "classnames";
import { NavLink } from "react-router-dom";

import { getAuthClaims, getPlatform, getUser } from "api/methods";
import { AdminImpersonationBanner } from "molecules/admin-impersonation-banner/AdminImpersonationBanner";
import { TopNavActions } from "molecules/navbars/TopNavActions";

import { DesktopRewards } from "./DesktopRewards";
import { usePrimaryNavItems } from "../usePrimaryNavItems";

export const DesktopHeader = () => {
  const items = usePrimaryNavItems();

  const { data: platform } = useQuery({ ...getPlatform.keyed() });

  const { data: authClaims } = useQuery({ ...getAuthClaims.keyed() });
  const { data: user } = useQuery({
    ...getUser.keyed(),
    staleTime: 1000 * 60 * 2,
  });

  const isAdminImpersonation = !!authClaims?.claims.adminUserId;

  return (
    <header className="sticky top-0 z-30 hidden border-b border-outline-grey bg-white pt-[env(safe-area-inset-top)] desk:block">
      {isAdminImpersonation && !!user && (
        <AdminImpersonationBanner
          platformUserDisplayName={
            user.email || (user.firstName && user.lastName)
              ? user.email || `${user.firstName} ${user.lastName}`
              : user.phoneNumber
          }
        />
      )}
      <div className="mx-auto flex h-16 w-full max-w-6xl items-center gap-2 px-6 desklg:h-[72px] desklg:gap-6 desklg:px-8">
        <NavLink
          to="/home"
          className="flex shrink-0 items-center"
          aria-label={platform?.name ?? ""}
        >
          {platform?.logoImageUrl ? (
            <img src={platform.logoImageUrl} alt={platform.name ?? ""} className="h-8 w-auto" />
          ) : (
            <span className="font-serif text-h3 text-primary">{platform?.name}</span>
          )}
        </NavLink>

        <nav className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto desklg:gap-2">
          {items.map((item) => (
            <NavLink
              key={item.key}
              to={item.to}
              onTouchStart={item.intentPreload}
              onMouseEnter={item.intentPreload}
              onFocus={item.intentPreload}
              className={({ isActive: navLinkIsActive }) => {
                const isActive = item.isActiveOverride ?? navLinkIsActive;
                return cx(
                  "relative whitespace-nowrap rounded-full px-2 py-2 text-bold2 transition-colors desklg:px-4",
                  isActive
                    ? "bg-brand-color/10 text-brand-color"
                    : "text-secondary hover:bg-background-two hover:text-primary",
                );
              }}
            >
              {item.label}
              {item.hasBadge && (
                <span className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-brand-color desklg:right-2.5" />
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex shrink-0 items-center">
          <TopNavActions
            accountAction={
              platform?.platformSettings?.rewardsEnabled ? <DesktopRewards /> : undefined
            }
          />
        </div>
      </div>
    </header>
  );
};
